import { Flex, Box, Image, Text, Button } from 'rebass';
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import Modal from 'app/modal/v3/index';
import ResponsiveReactPlayer from 'story-media/medium-types/ResponsiveReactPlayer';
import { DateTime } from 'luxon';
import { useParams } from 'react-router-dom';
import ViewRealWMG from './assets/ViewWMG.png';
import QuickGuide from './assets/QuickGuide.png';
import MediaFtue from './assets/MediaFtue.png';
import { useCollectionTitle } from 'collections/shared/title';
import useCollectionCover from 'collections/hooks/cover';
import useEntity from 'entities/hooks/use-entity';
import store from 'store';

const VIDEO_MONTAGE_EXAMPLE_URL = 'https://vimeo.com/1119228949?share=copy&fl=sv&fe=ci';

function LetsBeginFooterComponent({ disabled, sx = {}, backText = 'Back', onBackDisabled, onBack, nextButtonText, onStepUp, buttonSx = {} }) {
    return <Flex
        width="100%"
        sx={{
            flexDirection: 'column-reverse',
            justifyContent: "space-around",
            alignItems: "center",
            px: "26px",
            pt: 2,
            gap: 2,
            ...sx,
        }}
    >

        {onBack && <Button
            variant="outline"
            width="max-content"
            height={40}
            onClick={onBackDisabled || !onBack ? undefined : onBack}
            disabled={onBackDisabled}
            sx={{ borderRadius: '23px', opacity: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
            <Text color="#9B59B6" minWidth={'max-content'} fontFamily="Poppins" lineHeight={'150%'} ml={2} fontWeight={400} fontSize="15px">
                {backText}
            </Text>
        </Button>}
        <Button
            height={40}
            width="max-content"
            py={1}
            bg={disabled ? "grey" : "brand"}
            onClick={onStepUp}
            sx={{ borderRadius: '23px', opacity: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', ...buttonSx }}
        >
            {nextButtonText}
        </Button>


    </Flex>


}

const FirstTimeUserExperience = ({ isForMedia = false, setShowFTUE }) => {
    const [step, setStep] = useState(1);
    const [showExampleVideo, setShowExampleVideo] = useState(false);

    const { id, type } = useParams();
    const modalRef = useRef(null);
    const onStepUp = () => setStep(step + 1)

    // Closing the in-app video montage example advances the FTUE the same way the
    // old "opens in a new tab" click did (which never blocked the flow).
    const closeExampleVideo = () => {
        setShowExampleVideo(false);
        onStepUp();
    };
    const { entity } = useEntity({ id, type });

    const { 'first-name-possessive': firstNamePossessive } = entity
    const title = useCollectionTitle({ id, type });
    const coverUrl = useCollectionCover({ id, type });

    const parsedended = DateTime.now();
    const formattedbegan = parsedended.toFormat('LLL d, yyyy');


    useEffect(() => {
        if (modalRef.current)
            modalRef.current.scrollTo(0, 0);

    }, [step]);

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <Flex m="auto" mt={[2, "50px"]} flexDirection="column" justifyContent="center" alignItems="center" maxWidth={500}
                        ref={modalRef}
                        // maxHeight={["80vh", "100%"]}
                        overflow="auto" mb={7}>
                        <Flex px={2} flexDirection="column">
                            <Text fontFamily="Bitter" textAlign="center" fontWeight="normal"
                                sx={{
                                    color: '#000',
                                    fontFamily: 'Bitter',
                                    fontSize: '30px',
                                    fontWeight: '500',
                                    lineHeight: '165%',
                                }}
                            >
                                Your Baby’s Story, Month by Month
                            </Text>
                            <Image src={ViewRealWMG} />
                            <Text
                                my={2}
                                color="#64748B"
                                fontFamily="Poppins"
                                fontSize="18px"
                                fontStyle="normal"
                                fontWeight={400}
                                lineHeight="200%"
                                letterSpacing="-0.36px"
                                textAlign='left'
                            >
                                Upload a monthly photo and video to get two custom book pages with a QR-linked video montage, plus a printable keepsake PDF to frame or gift.
                            </Text>

                            <LetsBeginFooterComponent
                                sx={{ justifyContent: 'center' }}
                                onStepUp={() => setShowExampleVideo(true)}
                                nextButtonText="Watch a video montage example"
                                backText='No thanks, I’m good'
                                onBack={() => {
                                    onStepUp()
                                }}
                            />
                        </Flex>
                    </Flex>
                );
            case 2:
                return (
                    <Flex m="auto" mt={[2, "50px"]} mb={7} flexDirection="column" justifyContent="center" alignItems="center" maxWidth={500}>
                        <Image src={QuickGuide} />
                        <Flex px={2} flexDirection="column">
                            <Text fontFamily="Bitter" textAlign="left" fontWeight="normal"
                                sx={{
                                    color: '#000',
                                    fontFamily: 'Bitter',
                                    fontSize: '30px',
                                    fontWeight: '500',
                                    lineHeight: '165%',
                                }}
                            >
                                Quick Guide
                            </Text>
                            <Text
                                my={2}
                                color="#64748B"
                                fontFamily="Poppins"
                                fontSize="18px"
                                fontStyle="normal"
                                fontWeight={400}
                                lineHeight="200%"
                                letterSpacing="-0.36px"
                                textAlign='left'
                            >
                                Like the image above, get started by adding a photo and short video of your baby.
                            </Text>
                            <LetsBeginFooterComponent
                                sx={{ justifyContent: 'center' }}
                                onStepUp={() => {
                                    store.set('wmg_ftui_watched', true)
                                    setShowFTUE(false)
                                }}
                                nextButtonText="Get started"
                            />
                        </Flex>
                    </Flex >
                )
            default:
                return null;
        }
    }

    return (
        <>
        <Modal
            maxWidth={620}
            shadeProps={{ pt: [0, 4] }}
            closeOnOutsideClick={false}
            isScrollActive={false}
            aboveTooltip
        >
            {isForMedia ?
                <Flex m="auto" mt={[2, "50px"]} flexDirection="column" justifyContent="center" alignItems="center" maxWidth={500} mb={7}>
                    <Image src={MediaFtue} />
                    <Flex px={2} flexDirection="column">
                        <Text fontFamily="Bitter" textAlign="left" fontWeight="normal"
                            sx={{
                                color: '#000',
                                fontFamily: 'Bitter',
                                fontSize: '30px',
                                fontWeight: '500',
                                lineHeight: '165%',
                            }}
                        >
                            See {firstNamePossessive} Watch Me Grow pages & video unfold!
                        </Text>
                        <Text
                            my={2}
                            color="#64748B"
                            fontFamily="Poppins"
                            fontSize="18px"
                            fontStyle="normal"
                            fontWeight={400}
                            lineHeight="200%"
                            letterSpacing="-0.36px"
                            textAlign='left'
                        >
                            View your latest changes in your Book Preview and Video preview tabs.
                        </Text>
                        <LetsBeginFooterComponent
                            sx={{ justifyContent: 'center' }}
                            onStepUp={() => setShowFTUE(false)}
                            nextButtonText="Sounds great!"
                        />
                    </Flex>
                </Flex >

                : renderStep()}
        </Modal>

        {/* In-app video montage example — replaces the old window.open('_blank') tab.
            Rendered as a sibling AFTER the FTUE modal so it stacks on top of it
            (same z-index, later in DOM). Full-screen on mobile, 700px on desktop. */}
        {showExampleVideo && (
            <Modal
                noHeader
                maxWidth={900}
                height={['unset', 'auto']}
                borderRadius={[0, 8]}
                shadeProps={{ p: [0, 4], pt: [0, 4] }}
                closeOnOutsideClick={false}
                isScrollActive={false}
                aboveTooltip
                childSx={{ flex: '1 1 auto', display: 'flex' }}
            >
                <Flex
                    flexDirection="column"
                    justifyContent="center"
                    bg="black"
                    sx={{ position: 'relative', width: '100%', height: ['100vh', '400px'] }}
                >
                    <Box
                        as="button"
                        type="button"
                        onClick={closeExampleVideo}
                        aria-label="Close"
                        sx={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            zIndex: 2,
                            background: 'rgba(0,0,0,0.55)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: '#fff',
                        }}
                    >
                        <FiX size={20} />
                    </Box>
                    <ResponsiveReactPlayer
                        url={VIDEO_MONTAGE_EXAMPLE_URL}
                        playerWrapper={{ width: '100%', height: '100%' }}
                    />
                </Flex>
            </Modal>
        )}
        </>
    );

};

FirstTimeUserExperience.defaultProps = {
    title: 'Add Media',
    dividerText: 'other types',
    pagePosition: null,
};

FirstTimeUserExperience.propTypes = {
    title: PropTypes.string,
    onClose: PropTypes.func.isRequired,
};

export default FirstTimeUserExperience;
